import type { ComponentType } from "react";
import AccountCircleOutlined from "@mui/icons-material/AccountCircleOutlined";
import FactCheckOutlined from "@mui/icons-material/FactCheckOutlined";
import GroupsOutlined from "@mui/icons-material/GroupsOutlined";
import HomeOutlined from "@mui/icons-material/HomeOutlined";
import ManageAccountsOutlined from "@mui/icons-material/ManageAccountsOutlined";
import PersonSearchOutlined from "@mui/icons-material/PersonSearchOutlined";
import SettingsOutlined from "@mui/icons-material/SettingsOutlined";
import { Box, List, Stack, useTheme, type SvgIconProps } from "@mui/material";
import { useTranslation } from "react-i18next";
import { Link as RouterLink, useLocation } from "react-router-dom";

import { AppDrawerListItem } from "@thalamos/frontend";
import type { SessionContext } from "@thalamos/haven-common";
import {
  customerRolesOf,
  isRoleAbleToApprove,
  isThalamosAdministrationSessionContext,
  mayActInPersonDirectory,
} from "@thalamos/haven-common";

import { SignOutButton } from "../components/SignOutButton/SignOutButton.js";
import { useUser } from "../Context/UserContext.js";
import { createRoute } from "../routing/createRoute.js";
import { SessionContextSwitcher } from "./SessionContextSwitcher.js";

/**
 * A place the shell can navigate to. `section` is the path it owns, which is
 * not always where the link goes — `/admin` opens on the directory.
 */
export type NavItem = {
  key: string;
  to: string;
  section: string;
  /** A translation key, so the navigation holds no wording of its own. */
  label: string;
  Icon: ComponentType<SvgIconProps>;
};

const HOME: NavItem = {
  key: "home",
  to: createRoute.home(),
  section: createRoute.home(),
  label: "home.title",
  Icon: HomeOutlined,
};

const PERSONS: NavItem = {
  key: "persons",
  to: createRoute.persons(),
  section: createRoute.persons(),
  label: "personListing.title",
  Icon: GroupsOutlined,
};

const MATCH_PERSON: NavItem = {
  key: "match-person",
  to: createRoute.matchPerson(),
  section: createRoute.matchPerson(),
  label: "personMatch.title",
  Icon: PersonSearchOutlined,
};

const USERS: NavItem = {
  key: "users",
  to: createRoute.users(),
  section: createRoute.users(),
  label: "admin.users.title",
  Icon: ManageAccountsOutlined,
};

const APPROVALS: NavItem = {
  key: "approvals",
  to: createRoute.approvals(),
  section: createRoute.approvals(),
  label: "approvals.title",
  Icon: FactCheckOutlined,
};

const ACCOUNT: NavItem = {
  key: "profile",
  to: createRoute.profile(),
  section: createRoute.profile(),
  label: "profile.openYours",
  Icon: AccountCircleOutlined,
};

const ADMINISTRATION: NavItem = {
  key: "admin",
  to: createRoute.adminUsers(),
  section: createRoute.admin(),
  label: "admin.title",
  Icon: SettingsOutlined,
};

/**
 * The administrative membership is held at Thalamos rather than at a customer,
 * so it has no young people to list. A session with no session context still
 * reaches its own account, which is why the shell renders for one at all.
 */
export const navItemsFor = (
  sessionContext: SessionContext | null,
): readonly NavItem[] => {
  if (isThalamosAdministrationSessionContext(sessionContext)) {
    return [ADMINISTRATION, ACCOUNT];
  }

  if (sessionContext === null) {
    return [HOME, ACCOUNT];
  }

  const mayDecide = customerRolesOf(sessionContext.roles).some(
    isRoleAbleToApprove,
  );

  return [
    HOME,
    PERSONS,
    ...(sessionContext.kind === "organisation_administration" ? [USERS] : []),
    ...(mayActInPersonDirectory(sessionContext, "match") ? [MATCH_PERSON] : []),
    ...(mayDecide ? [APPROVALS] : []),
    ACCOUNT,
  ];
};

const withinSection = (item: NavItem, pathname: string): boolean =>
  item.section === createRoute.home()
    ? pathname === createRoute.home()
    : pathname === item.section || pathname.startsWith(`${item.section}/`);

// One section's path can sit inside another's, so the most specific one wins.
const activeItem = (
  items: readonly NavItem[],
  pathname: string,
): NavItem | undefined =>
  items
    .filter((item) => withinSection(item, pathname))
    .reduce<NavItem | undefined>(
      (best, item) =>
        best === undefined || item.section.length > best.section.length
          ? item
          : best,
      undefined,
    );

/**
 * The sections the session can reach, and the one being looked at. With nobody
 * signed in there are none, as everything offered needs a session.
 */
export const useSessionContextNavItems = (): {
  items: readonly NavItem[];
  current: NavItem | undefined;
} => {
  const { user } = useUser();
  const { pathname } = useLocation();
  const items =
    user === undefined ? [] : navItemsFor(user.sessionContext ?? null);

  return { items, current: activeItem(items, pathname) };
};

/**
 * What the application drawer holds: the sections a session context can reach,
 * with the switcher and sign-out at its foot. Sets no background, width or
 * breakpoints of its own; those belong to the drawer it is placed in.
 */
export const SessionContextNavigation = () => {
  const { t } = useTranslation();
  const { palette } = useTheme();
  const { user } = useUser();
  const { items, current } = useSessionContextNavItems();
  const signedIn = user !== undefined;

  return (
    <Stack sx={{ flexGrow: 1, width: "100%", minHeight: 0 }}>
      <List
        sx={{
          flexGrow: 1,
          overflowY: "auto",
          px: 1,
          "& .MuiListItemButton-root": { borderRadius: 1 },
          "& .MuiListItemIcon-root": { minWidth: 36 },
          "& .MuiListItemText-primary": { fontSize: 14 },
        }}
      >
        {items.map((item) => {
          const active = item === current;

          return (
            <AppDrawerListItem
              key={item.key}
              component={RouterLink}
              to={item.to}
              icon={<item.Icon />}
              text={t(item.label)}
              selected={active}
              aria-current={active ? "page" : undefined}
              hoverColor={palette.drawer.hoveredItem}
              selectedColor={palette.drawer.activeItem}
            />
          );
        })}
      </List>

      {signedIn ? (
        <Stack spacing={1.5} alignItems="center" sx={{ px: 1, pb: 2 }}>
          <SessionContextSwitcher />
          {/* Drawn on the drawer's colour, so it takes the drawer's foreground
              and width rather than what a MUI button assumes. */}
          <Box
            sx={{
              width: "100%",
              "& .MuiButtonBase-root": {
                color: "inherit",
                width: "100%",
                fontSize: 12,
                fontWeight: 600,
                letterSpacing: "0.5px",
              },
            }}
          >
            <SignOutButton />
          </Box>
        </Stack>
      ) : null}
    </Stack>
  );
};
